import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Zap, AlertTriangle, CheckCircle, Clock, TrendingUp } from "lucide-react";
import { api, getActiveSubdomain } from "@/lib/api";

function ExpiryBadge({ planEnd }) {
  if (!planEnd) return <span className="text-xs text-slate-400">No plan</span>;
  const today = new Date();
  const end = new Date(planEnd);
  const days = Math.ceil((end - today) / 86400000);
  if (days < 0) return <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">Expired</span>;
  if (days <= 7) return <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">{days}d left ⚠️</span>;
  if (days <= 30) return <span className="text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">{days}d left</span>;
  return <span className="text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full">{days}d left ✓</span>;
}

export default function CustomerDashboard() {
  const { subdomain: paramSubdomain } = useParams();
  const activeSubdomain = getActiveSubdomain();
  const subdomain = activeSubdomain || paramSubdomain;
  const base = activeSubdomain ? "/customer" : `/site/${subdomain}/customer`;
  const [ids, setIds] = useState([]);
  const [recharges, setRecharges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/customer/ids"),
      api.get("/customer/recharges"),
    ]).then(([r1, r2]) => {
      setIds(r1.data); setRecharges(r2.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="pt-8 text-center text-slate-400">Loading your dashboard…</div>;

  const urgentConnections = ids.filter(c => {
    if (!c.subscription?.plan_end) return false;
    const days = Math.ceil((new Date(c.subscription.plan_end) - new Date()) / 86400000);
    return days <= 7;
  });

  return (
    <div className="space-y-6 pt-6">
      {/* Expiry Alerts */}
      {urgentConnections.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-700">Action needed!</p>
            <p className="text-xs text-red-600 mt-0.5">
              {urgentConnections.length} connection{urgentConnections.length > 1 ? 's' : ''} expiring in ≤7 days.
            </p>
            <Link to={`${base}/recharge`} className="mt-2 inline-block text-xs font-bold text-red-700 underline">Recharge now →</Link>
          </div>
        </div>
      )}

      {/* Quick Recharge CTA */}
      <Link to={`${base}/recharge`}
        className="block bg-gradient-to-br from-brand-primary to-brand-accent rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium opacity-80">Quick Action</p>
            <p className="text-2xl font-heading font-bold mt-1">Recharge DTH</p>
            <p className="text-sm opacity-70 mt-1">Fast, secure, instant activation</p>
          </div>
          <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
            <Zap className="w-7 h-7" />
          </div>
        </div>
      </Link>

      {/* My Connections */}
      <div>
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">My Connections</h2>
        {ids.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center text-slate-400 text-sm">
            No DTH connections found. Contact your operator.
          </div>
        ) : (
          <div className="space-y-3">
            {ids.map(conn => (
              <div key={conn.id} className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-brand-nav">{conn.operator}</p>
                    <p className="text-xs text-slate-400 font-mono mt-0.5">ID: {conn.dth_id}</p>
                  </div>
                  <ExpiryBadge planEnd={conn.subscription?.plan_end} />
                </div>
                {conn.subscription && (
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-3 text-xs text-slate-500">
                    <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                    <span>Plan ends: <strong>{conn.subscription.plan_end || '—'}</strong></span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Recharges */}
      {recharges.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Recent Activity</h2>
            <Link to={`${base}/history`} className="text-xs text-brand-primary font-semibold">View all →</Link>
          </div>
          <div className="space-y-2">
            {recharges.slice(0, 3).map(r => (
              <div key={r.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${r.status === 'success' ? 'bg-green-50' : 'bg-slate-100'}`}>
                    {r.status === 'success' ? <TrendingUp className="w-4 h-4 text-green-500" /> : <Clock className="w-4 h-4 text-slate-400" />}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-700">₹{r.amount}</p>
                    <p className="text-xs text-slate-400">{r.created_at?.slice(0, 10)}</p>
                  </div>
                </div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${r.status === 'success' ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                  {r.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
